import {
  ArrowLeft,
  Building2,
  CircleAlert,
  FileText,
  ListTodo,
  ReceiptText,
  Tags,
  Users,
} from "lucide-react";
import { Link, useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import { formatBRL } from "@yatta/domain";
import { Badge, Button, Card, Skeleton } from "../components/ui";
import { useCustomer360Customer } from "../data/supergestor-data";
import { env } from "../lib/env";
import { useFakeCustomerStoreVersion } from "../data/fake-customer-store";
import {
  getFakeCustomerRecord,
  resolveSellerName,
  updateFakeCustomerRecord,
} from "../data/fake-customer-store";
import { useTeamData } from "../hooks/usePlatformData";
import type { CustomerOrigin } from "@yatta/domain";

export function Customer360Page() {
  const { id } = useParams();
  const query = useCustomer360Customer(id);
  const teamQuery = useTeamData();
  useFakeCustomerStoreVersion();
  const [editing, setEditing] = useState(false);
  const [editName, setEditName] = useState("");
  const [editOrigin, setEditOrigin] = useState<CustomerOrigin>("PREEXISTING");
  const [editSeller, setEditSeller] = useState("");
  const [notice, setNotice] = useState("");
  useEffect(() => {
    if (!id || !editing) return;
    const record = getFakeCustomerRecord(id);
    if (!record) return;
    setEditName(record.holderName);
    setEditOrigin(record.customerOrigin);
    setEditSeller(record.responsibleSellerId);
    setNotice("");
  }, [editing, id]);
  if (query.isLoading) {
    return (
      <div className="query-state" role="status" aria-label="Carregando Customer 360">
        <Skeleton className="detail-skeleton" />
      </div>
    );
  }
  if (query.error)
    return (
      <div className="query-state query-error" role="alert">
        <CircleAlert size={20} aria-hidden="true" />
        <div>
          <strong>Não foi possível carregar o cliente.</strong>
          <span>Tente novamente em alguns instantes.</span>
          <Link className="button button-secondary" to="/clientes">
            Voltar para clientes
          </Link>
        </div>
      </div>
    );
  const customer = query.data;
  if (!customer)
    return (
      <div className="empty-state">
        <strong>Cliente não encontrado</strong>
        <p>O cadastro informado não está disponível ou não pertence a esta base.</p>
        <Link className="button button-secondary" to="/clientes">
          Voltar para clientes
        </Link>
      </div>
    );
  const fakeRecord = env.useMocks && id ? getFakeCustomerRecord(id) : null;
  const riskTone =
    customer.riskLevel === "HIGH" || customer.riskLevel === "CRITICAL"
      ? "danger"
      : customer.riskLevel === "MEDIUM"
        ? "warning"
        : "success";
  return (
    <div className="page-stack">
      <Link className="back-link" to="/clientes">
        <ArrowLeft size={16} /> Voltar
      </Link>
      <Card>
        <div className="customer-hero">
          <div className="large-avatar">
            {customer.kind === "LEGAL_ENTITY" ? <Building2 size={24} /> : <Users size={24} />}
          </div>
          <div>
            <p className="eyebrow">CUSTOMER 360</p>
            <h2>{customer.displayName}</h2>
            <p>
              {customer.identifier} · {customer.contact}
            </p>
            <div className="badge-row">
              <Badge tone="info">
                {customer.customerOrigin === "PREEXISTING"
                  ? "Cliente preexistente"
                  : customer.customerOrigin === "NEW_ACQUISITION"
                    ? "Nova aquisição"
                    : "Origem não informada"}
              </Badge>
              <span className="muted">
                Responsável: {customer.responsibleSeller ?? "Não atribuído"}
              </span>
            </div>
          </div>
          <div className="customer-actions">
            <Button
              variant="secondary"
              onClick={() => setEditing((value) => !value)}
              disabled={!env.useMocks}
            >
              {editing ? "Cancelar edição" : "Editar cadastro"}
            </Button>
          </div>
        </div>
        {editing && fakeRecord && (
          <form
            className="form-section compact-form-section"
            onSubmit={(event) => {
              event.preventDefault();
              if (editName.trim().length < 3 || !editSeller) {
                setNotice("Informe o nome e o vendedor responsável.");
                return;
              }
              const saved = updateFakeCustomerRecord(id!, {
                holderName: editName.trim(),
                customerOrigin: editOrigin,
                responsibleSellerId: editSeller,
              });
              setNotice(
                saved
                  ? "Cadastro atualizado nesta sessão de demonstração."
                  : "Cliente não encontrado.",
              );
              if (saved) setEditing(false);
            }}
          >
            <div className="form-grid">
              <label className="field">
                <span className="field-label">Nome do titular</span>
                <input value={editName} onChange={(event) => setEditName(event.target.value)} />
              </label>
              <label className="field">
                <span className="field-label">Origem do cliente</span>
                <select
                  value={editOrigin}
                  onChange={(event) => setEditOrigin(event.target.value as CustomerOrigin)}
                >
                  <option value="PREEXISTING">Cliente preexistente</option>
                  <option value="NEW_ACQUISITION">Nova aquisição</option>
                </select>
              </label>
              <label className="field">
                <span className="field-label">Vendedor responsável</span>
                <select value={editSeller} onChange={(event) => setEditSeller(event.target.value)}>
                  <option value="">Selecione um vendedor</option>
                  {(teamQuery.data ?? [])
                    .filter((member) => member.status === "ATIVO")
                    .map((member) => (
                      <option key={member.id} value={member.id}>
                        {member.name}
                      </option>
                    ))}
                </select>
              </label>
            </div>
            {notice && (
              <p className="form-status" role="status">
                {notice}
              </p>
            )}
            <div className="card-actions">
              <Button type="button" variant="secondary" onClick={() => setEditing(false)}>
                Cancelar
              </Button>
              <Button type="submit" disabled={teamQuery.isLoading}>
                Salvar alterações demo
              </Button>
            </div>
          </form>
        )}
      </Card>
      <nav className="tabs" aria-label="Seções do Customer 360">
        <a href="#resumo">Resumo</a>
        <a href="#pessoas">Pessoas</a>
        <a href="#beneficios">Benefícios</a>
        <a href="#documentos">Documentos</a>
        <a href="#historico">Histórico</a>
      </nav>
      <section className="metric-grid metric-grid-four">
        <article className="metric-card">
          <ReceiptText size={20} />
          <span>Saldo em aberto</span>
          <strong>{formatBRL(BigInt(customer.outstandingCents))}</strong>
          <small>{customer.openInvoices} cobrança(s)</small>
        </article>
        <article className="metric-card">
          <FileText size={20} />
          <span>Contratos e acordos</span>
          <strong>{customer.contracts + customer.agreements}</strong>
          <small>{customer.subscriptions} assinatura(s)</small>
        </article>
        <article className="metric-card">
          <ListTodo size={20} />
          <span>Tarefas abertas</span>
          <strong>{customer.openTasks}</strong>
          <small>Ações pendentes</small>
        </article>
        <article className="metric-card">
          <Tags size={20} />
          <span>Risco</span>
          <Badge tone={riskTone}>{customer.riskLevel}</Badge>
          <small>{customer.financialStatus}</small>
        </article>
      </section>
      <div className="two-column" id="resumo">
        <Card title="Dados do cadastro">
          <div className="member-list">
            <div className="member-row">
              <div>
                <strong>Cadastro</strong>
                <span>{customer.registrationStatus ?? "Cadastrado"}</span>
              </div>
              <Badge tone="success">{customer.registrationStatus ?? "ATIVO"}</Badge>
            </div>
            <div className="member-row">
              <div>
                <strong>Contrato</strong>
                <span>Estado independente do cadastro</span>
              </div>
              <Badge tone="info">{customer.contractStatus ?? "Sem contrato"}</Badge>
            </div>
            <div className="member-row">
              <div>
                <strong>Acesso</strong>
                <span>Não determina benefício</span>
              </div>
              <Badge tone="info">{customer.accessStatus ?? "Não iniciado"}</Badge>
            </div>
            <div className="member-row">
              <div>
                <strong>Benefício</strong>
                <span>Elegibilidade e ativação separadas</span>
              </div>
              <Badge tone="info">{customer.benefitStatus ?? "Não ativado"}</Badge>
            </div>
          </div>
        </Card>
        <Card title="Linha do tempo">
          <ol className="timeline">
            <li>
              <strong>Customer 360 atualizado</strong>
              <span>Identidades, contratos e recebíveis consolidados</span>
            </li>
            <li>
              <strong>Última movimentação</strong>
              <span>
                {customer.lastEventAt
                  ? new Date(customer.lastEventAt).toLocaleString("pt-BR")
                  : "Sem evento"}
              </span>
            </li>
            <li>
              <strong>Verificação de duplicidade</strong>
              <span>Nenhuma fusão automática pendente</span>
            </li>
          </ol>
        </Card>
      </div>
      <section className="two-column" id="pessoas">
        <Card
          title="Pessoas da família"
          description="Titular e dependentes do cadastro demonstrativo."
        >
          <div className="member-list">
            <div className="member-row">
              <div>
                <strong>{fakeRecord?.holderName ?? customer.displayName}</strong>
                <span>Titular da conta</span>
              </div>
              <Badge tone="info">Titular</Badge>
            </div>
            <div className="member-row">
              <div>
                <strong>{customer.dependents ?? 0} dependente(s)</strong>
                <span>Quantidade informada no cadastro</span>
              </div>
              <Badge tone="neutral">Demonstração</Badge>
            </div>
          </div>
        </Card>
        <Card title="Responsabilidade comercial">
          <div className="member-row">
            <div>
              <strong>{resolveSellerName(fakeRecord?.responsibleSellerId)}</strong>
              <span>Vendedor responsável</span>
            </div>
            <Badge tone="success">Atribuído</Badge>
          </div>
        </Card>
      </section>
      <section id="beneficios">
        <Card
          title="Benefícios e elegibilidade"
          description="Estados demonstrativos; nenhuma ativação externa é executada."
        >
          <div className="member-list">
            <div className="member-row">
              <div>
                <strong>Plano funerário</strong>
                <span>Elegibilidade demonstrativa</span>
              </div>
              <Badge tone="info">{fakeRecord?.funeralStatus ?? "Não informado"}</Badge>
            </div>
            <div className="member-row">
              <div>
                <strong>Telemedicina</strong>
                <span>Integração MobiMedical em modo fake</span>
              </div>
              <Badge tone="info">{fakeRecord?.telemedicineStatus ?? "Não informado"}</Badge>
            </div>
          </div>
        </Card>
      </section>
      <section id="documentos">
        <Card title="Documentos" description="Nenhum arquivo real é armazenado nesta demonstração.">
          <p className="muted">
            A geração de contrato demonstrativo pode ser iniciada na área Contratos. Assinatura
            externa permanece desativada.
          </p>
          <Link className="button button-secondary" to="/contratos">
            Abrir contratos
          </Link>
        </Card>
      </section>
      <section id="historico">
        <Card
          title="Histórico de atividade"
          description="Atividade visível neste cadastro fictício."
        >
          <ol className="timeline">
            <li>
              <strong>Cadastro demonstrativo disponível</strong>
              <span>
                {customer.lastEventAt
                  ? new Date(customer.lastEventAt).toLocaleString("pt-BR")
                  : "Sem data de evento"}
              </span>
            </li>
            <li>
              <strong>Origem</strong>
              <span>
                {customer.customerOrigin === "PREEXISTING"
                  ? "Cliente preexistente"
                  : "Nova aquisição"}
              </span>
            </li>
            <li>
              <strong>Próxima ação</strong>
              <span>
                {customer.openTasks
                  ? `${customer.openTasks} pendência(s) operacional(is)`
                  : "Sem pendências fictícias"}
              </span>
            </li>
          </ol>
        </Card>
      </section>
    </div>
  );
}
